import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  generateQuestions,
  ROUND_DURATION_MS, CORRECT_POINTS, WRONG_POINTS, FEEDBACK_DELAY_MS,
  saveHighScore, getRankTitle,
} from '../lib/game'
import {
  playCorrectSound, playWrongSound, playTickSound, playFanfareSound,
} from '../lib/sound'
import { useFullscreen } from '../lib/fullscreen'

// ─── Sub-components ─────────────────────────────────────────────────────────

/** Top 60-second Timer Bar */
function TimerHeader({ leftMs, totalMs = ROUND_DURATION_MS }) {
  const secondsStr = (leftMs / 1000).toFixed(2)
  const ratio = Math.max(0, leftMs / totalMs)
  const isLow = leftMs <= 10_000

  return (
    <div className="w-full flex flex-col items-center gap-1 select-none">
      <div className="flex items-center justify-between w-full px-4 text-xs font-semibold">
        <span style={{ color: 'rgba(255,255,255,0.5)' }}>⏱️ เวลาเหลือ:</span>
        <span
          className="font-mono text-base font-black transition-colors"
          style={{
            color: isLow ? '#ef4444' : '#06b6d4',
            textShadow: isLow ? '0 0 12px rgba(239,68,68,0.8)' : '0 0 12px rgba(6,182,212,0.5)',
            animation: isLow ? 'twinkle 0.5s ease-in-out infinite' : 'none',
          }}
        >
          {secondsStr}s
        </span>
      </div>
      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-100"
          style={{
            width: `${ratio * 100}%`,
            background: isLow
              ? 'linear-gradient(90deg, #ef4444, #f97316)'
              : 'linear-gradient(90deg, #7c3aed, #06b6d4)',
            boxShadow: isLow ? '0 0 10px #ef4444' : '0 0 10px #06b6d4',
          }}
        />
      </div>
    </div>
  )
}

/** Question card (type badge + numbers with distinct colors for GCD vs LCM) */
function QuestionCard({ question, compact }) {
  const isHrm  = question.type === 'hrm'
  const label  = isHrm ? '🟣 ห.ร.ม. (GCD)' : '🔵 ค.ร.น. (LCM)'
  const color  = isHrm ? '#c4b5fd' : '#67e8f9'
  const border = isHrm ? 'rgba(167, 139, 250, 0.7)' : 'rgba(6, 182, 212, 0.7)'
  const bg     = isHrm
    ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.38) 0%, rgba(76, 29, 149, 0.5) 100%)'
    : 'linear-gradient(135deg, rgba(6, 182, 212, 0.38) 0%, rgba(14, 116, 144, 0.5) 100%)'
  const glow   = isHrm
    ? '0 0 35px rgba(124, 58, 237, 0.45), inset 0 1px 0 rgba(255,255,255,0.2)'
    : '0 0 35px rgba(6, 182, 212, 0.45), inset 0 1px 0 rgba(255,255,255,0.2)'
  const nums   = question.numbers.join(', ')

  return (
    <div
      className="glass-card select-none w-full transition-all duration-300 flex flex-col items-center justify-center"
      style={{
        padding: compact ? '0.75rem 1rem' : '1.25rem 2rem',
        textAlign: 'center',
        border: `2px solid ${border}`,
        background: bg,
        boxShadow: glow,
        borderRadius: compact ? 20 : 28,
      }}
    >
      {/* Type badge */}
      <span
        style={{
          display: 'inline-block',
          padding: compact ? '4px 14px' : '5px 18px',
          borderRadius: 999,
          background: isHrm ? 'rgba(124, 58, 237, 0.5)' : 'rgba(6, 182, 212, 0.5)',
          border: `1.5px solid ${border}`,
          color: '#fff',
          fontSize: compact ? '0.8rem' : '0.95rem',
          fontWeight: 800,
          letterSpacing: '0.08em',
          marginBottom: '0.3rem',
        }}
      >
        {label}
      </span>

      {/* Prompt */}
      <div style={{ fontSize: compact ? '0.85rem' : '1.15rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginBottom: '0.2rem' }}>
        หา {isHrm ? 'ห.ร.ม.' : 'ค.ร.น.'} ของ
      </div>

      {/* BIG Question Numbers */}
      <div
        style={{
          fontSize: compact ? 'clamp(2rem, 5.5vw, 2.8rem)' : 'clamp(3rem, 8vw, 4.5rem)',
          fontWeight: 900,
          color: '#ffffff',
          fontFamily: 'var(--font-outfit)',
          letterSpacing: '0.06em',
          textShadow: `0 0 30px ${color}, 0 0 60px ${color}88`,
          lineHeight: 1.15,
          padding: compact ? '0.3rem 0.8rem' : '0.5rem 1.2rem',
          background: 'rgba(0, 0, 0, 0.35)',
          borderRadius: compact ? 14 : 20,
          border: '1px solid rgba(255, 255, 255, 0.15)',
          marginTop: '0.2rem',
          width: '100%',
          maxWidth: compact ? '380px' : '560px',
        }}
      >
        {nums}
      </div>
    </div>
  )
}

/** A single answer choice button optimized for Touchscreen / Multi-touch */
function ChoiceButton({
  id, value, onClick, state, size = 'normal',
}) {
  const isLg = size === 'lg'
  const bg = state === 'correct' ? 'rgba(16,185,129,0.45)'
    : state === 'wrong'    ? 'rgba(239,68,68,0.45)'
    : state === 'reveal'   ? 'rgba(16,185,129,0.2)'
    : 'rgba(255,255,255,0.06)'

  const border = state === 'correct' ? '#10b981'
    : state === 'wrong'    ? '#ef4444'
    : state === 'reveal'   ? '#10b98166'
    : 'rgba(255,255,255,0.14)'

  const glow = state === 'correct'
    ? '0 0 24px rgba(16,185,129,0.6), 0 0 48px rgba(16,185,129,0.3)'
    : state === 'wrong'
    ? '0 0 24px rgba(239,68,68,0.5)'
    : 'none'

  const handlePointerDown = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (state === 'idle') {
      onClick()
    }
  }

  return (
    <button
      id={id}
      onPointerDown={handlePointerDown}
      onClick={(e) => {
        e.preventDefault()
        if (state === 'idle') onClick()
      }}
      disabled={state !== 'idle'}
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: isLg ? '88px' : '72px',
        padding: isLg ? '0.75rem 0.5rem' : '0.5rem 0.3rem',
        borderRadius: isLg ? 16 : 14,
        background: bg,
        border: `1.5px solid ${border}`,
        boxShadow: glow,
        color: '#fff',
        cursor: state === 'idle' ? 'pointer' : 'default',
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
      className="btn-neon select-none"
    >
      {/* Number */}
      <span
        style={{
          fontSize: isLg ? '1.85rem' : '1.4rem',
          fontWeight: 800,
          fontFamily: 'var(--font-outfit)',
          color: state === 'correct' ? '#10b981' : state === 'wrong' ? '#ef4444' : '#fff',
          textShadow: state === 'correct' ? '0 0 12px rgba(16,185,129,0.5)' : 'none',
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      {/* Reserved Badge Container (Always occupies fixed height to prevent size bounce) */}
      <div
        style={{
          height: isLg ? '22px' : '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '2px',
          visibility: (state === 'correct' || state === 'wrong') ? 'visible' : 'hidden',
        }}
      >
        {state === 'correct' && (
          <span style={{ fontSize: isLg ? '0.9rem' : '0.75rem', color: '#10b981', fontWeight: 900 }}>
            +2 ✓
          </span>
        )}
        {state === 'wrong' && (
          <span style={{ fontSize: isLg ? '0.9rem' : '0.75rem', color: '#ef4444', fontWeight: 900 }}>
            -1 ✗
          </span>
        )}
      </div>
    </button>
  )
}

/** Player panel for 2-player mode */
function PlayerPanel({
  player, playerState, question, rotate180 = false, onAnswer,
}) {
  const isP1 = player === 'p1'
  const accentColor = isP1 ? '#a78bfa' : '#06b6d4'
  const playerName = isP1 ? 'ผู้เล่น 1' : 'ผู้เล่น 2'

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '0.75rem',
        gap: '0.5rem',
        transform: rotate180 ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.4s ease',
        background: isP1
          ? 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, transparent 70%)'
          : 'linear-gradient(135deg, transparent 30%, rgba(6,182,212,0.08) 100%)',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
      className="select-none"
    >
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 10, height: 10, borderRadius: '50%',
              background: accentColor,
              boxShadow: `0 0 8px ${accentColor}`,
            }}
          />
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: accentColor }}>
            {playerName}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-outfit)' }}>
            ข้อ {playerState.qIdx + 1}
          </span>
        </div>

        {/* Score & Counters */}
        <div className="flex items-center gap-2">
          <div className="text-[0.7rem] text-white/50 flex gap-1 font-mono">
            <span className="text-emerald-400">✓{playerState.correctCount}</span>
            <span className="text-red-400">✗{playerState.wrongCount}</span>
          </div>
          <div
            className="glass-card"
            style={{ padding: '3px 12px', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>คะแนน</span>
            <span style={{
              fontSize: '1.2rem', fontWeight: 900,
              fontFamily: 'var(--font-outfit)',
              color: accentColor,
            }}>
              {playerState.score}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {question ? (
        <div className="flex-1 flex flex-col gap-3 justify-between">
          <QuestionCard question={question} compact />

          {/* Touch instruction / Feedback Message (Fixed Reserved Height) */}
          <div
            style={{
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            {playerState.result ? (
              <span
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  color: playerState.result.correct ? '#10b981' : '#ef4444',
                  padding: '2px 10px',
                  borderRadius: 99,
                  background: playerState.result.correct ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                  border: `1px solid ${playerState.result.correct ? '#10b98155' : '#ef444455'}`,
                }}
              >
                {playerState.result.correct
                  ? `✓ ถูกต้อง +2`
                  : `✗ ผิด -1 (คำตอบที่ถูกคือ ${question.answer})`}
              </span>
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>
                👉 แตะเลือกคำตอบบนหน้าจอ
              </span>
            )}
          </div>

          {/* Answer choices */}
          <div style={{ display: 'flex', gap: 6, width: '100%', touchAction: 'none' }}>
            {question.choices.map((choice, i) => {
              const answered = playerState.result
              let state = 'idle'
              if (answered !== null) {
                if (i === answered.choiceIndex) {
                  state = answered.correct ? 'correct' : 'wrong'
                } else if (choice === question.answer && playerState.advancing) {
                  state = 'reveal'
                }
              }
              return (
                <ChoiceButton
                  key={i}
                  id={`${player}-choice-${i}`}
                  value={choice}
                  onClick={() => onAnswer(i)}
                  state={state}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function GameScreen({ mode, topic = 'mix', difficulty = 'medium', onBackToHome }) {
  const [questions, setQuestions] = useState(() => generateQuestions(150, topic, difficulty))
  const [phase, setPhase]         = useState('ready')
  const [leftMs, setLeftMs]       = useState(ROUND_DURATION_MS)
  const { fullscreen, toggleFullscreen } = useFullscreen()

  // Single player state
  const [spQIdx, setSpQIdx]                 = useState(0)
  const [spResult, setSpResult]             = useState(null)
  const [spScore, setSpScore]               = useState(0)
  const [spCorrectCount, setSpCorrectCount] = useState(0)
  const [spWrongCount, setSpWrongCount]     = useState(0)

  // 2-Player independent states
  const [p1State, setP1State] = useState({
    qIdx: 0, score: 0, result: null, advancing: false, correctCount: 0, wrongCount: 0,
  })
  const [p2State, setP2State] = useState({
    qIdx: 0, score: 0, result: null, advancing: false, correctCount: 0, wrongCount: 0,
  })

  // 2-Player orientation & seating arrangement
  const [isPortrait, setIsPortrait] = useState(false)
  const [seating, setSeating]       = useState('opposite')

  useEffect(() => {
    const updateOrientation = () => {
      setIsPortrait(window.innerWidth < window.innerHeight)
    }
    updateOrientation()
    window.addEventListener('resize', updateOrientation)
    return () => window.removeEventListener('resize', updateOrientation)
  }, [])

  // Refs for timer loop & questions
  const questionsRef   = useRef(questions)
  const phaseRef       = useRef('ready')
  const spQIdxRef      = useRef(spQIdx)
  const spAdvancingRef = useRef(false)
  const startedAtRef   = useRef(null)
  const p1Ref          = useRef(p1State)
  const p2Ref          = useRef(p2State)

  questionsRef.current = questions
  phaseRef.current     = phase
  spQIdxRef.current    = spQIdx
  p1Ref.current        = p1State
  p2Ref.current        = p2State

  // ───────────────────────────────────────────────────────────────────────────
  // 60-SECOND ROUND TIMER LOGIC (Direct 60FPS loop)
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (phase !== 'playing') return

    startedAtRef.current = Date.now()
    let animId
    let lastTickSecond = Math.ceil(ROUND_DURATION_MS / 1000)

    const tick = () => {
      if (phaseRef.current !== 'playing') return

      const elapsed = Date.now() - startedAtRef.current
      const remaining = Math.max(0, ROUND_DURATION_MS - elapsed)
      setLeftMs(remaining)

      const currentSecond = Math.ceil(remaining / 1000)
      if (currentSecond !== lastTickSecond && remaining <= 10_000 && remaining > 0) {
        lastTickSecond = currentSecond
        playTickSound()
      }

      if (remaining <= 0) {
        playFanfareSound()
        setPhase('finished')
      } else {
        animId = requestAnimationFrame(tick)
      }
    }

    animId = requestAnimationFrame(tick)
    return () => {
      if (animId) cancelAnimationFrame(animId)
    }
  }, [phase])

  // ───────────────────────────────────────────────────────────────────────────
  // SINGLE PLAYER ANSWER LOGIC
  // ───────────────────────────────────────────────────────────────────────────

  const handleSpAnswer = useCallback((choiceIdx) => {
    if (phaseRef.current !== 'playing' || spAdvancingRef.current) return

    const q = questionsRef.current[spQIdxRef.current]
    if (!q) return

    spAdvancingRef.current = true
    const correct = q.choices[choiceIdx] === q.answer

    if (correct) {
      playCorrectSound()
      setSpScore(s => s + CORRECT_POINTS)
      setSpCorrectCount(c => c + 1)
      setSpResult({ choiceIndex: choiceIdx, correct, points: CORRECT_POINTS })
    } else {
      playWrongSound()
      setSpScore(s => s + WRONG_POINTS)
      setSpWrongCount(w => w + 1)
      setSpResult({ choiceIndex: choiceIdx, correct, points: WRONG_POINTS })
    }

    setTimeout(() => {
      if (phaseRef.current !== 'playing') return
      setSpQIdx(i => i + 1)
      setSpResult(null)
      spAdvancingRef.current = false
    }, FEEDBACK_DELAY_MS)
  }, [])

  // ───────────────────────────────────────────────────────────────────────────
  // 2-PLAYER ANSWER LOGIC
  // ───────────────────────────────────────────────────────────────────────────

  const handleP1Answer = useCallback((choiceIdx) => {
    if (phaseRef.current !== 'playing' || p1Ref.current.advancing) return

    const q = questionsRef.current[p1Ref.current.qIdx]
    if (!q) return

    const correct = q.choices[choiceIdx] === q.answer
    if (correct) {
      playCorrectSound()
    } else {
      playWrongSound()
    }

    setP1State(prev => ({
      ...prev,
      score: prev.score + (correct ? CORRECT_POINTS : WRONG_POINTS),
      correctCount: prev.correctCount + (correct ? 1 : 0),
      wrongCount: prev.wrongCount + (correct ? 0 : 1),
      result: { choiceIndex: choiceIdx, correct },
      advancing: true,
    }))

    setTimeout(() => {
      if (phaseRef.current !== 'playing') return
      setP1State(prev => ({
        ...prev,
        qIdx: prev.qIdx + 1,
        result: null,
        advancing: false,
      }))
    }, FEEDBACK_DELAY_MS)
  }, [])

  const handleP2Answer = useCallback((choiceIdx) => {
    if (phaseRef.current !== 'playing' || p2Ref.current.advancing) return

    const q = questionsRef.current[p2Ref.current.qIdx]
    if (!q) return

    const correct = q.choices[choiceIdx] === q.answer
    if (correct) {
      playCorrectSound()
    } else {
      playWrongSound()
    }

    setP2State(prev => ({
      ...prev,
      score: prev.score + (correct ? CORRECT_POINTS : WRONG_POINTS),
      correctCount: prev.correctCount + (correct ? 1 : 0),
      wrongCount: prev.wrongCount + (correct ? 0 : 1),
      result: { choiceIndex: choiceIdx, correct },
      advancing: true,
    }))

    setTimeout(() => {
      if (phaseRef.current !== 'playing') return
      setP2State(prev => ({
        ...prev,
        qIdx: prev.qIdx + 1,
        result: null,
        advancing: false,
      }))
    }, FEEDBACK_DELAY_MS)
  }, [])

  // Save High Scores when finished
  useEffect(() => {
    if (phase === 'finished') {
      if (mode === '1p') {
        saveHighScore('1p', spScore)
      } else {
        const topScore = Math.max(p1State.score, p2State.score)
        saveHighScore('2p', topScore)
      }
    }
  }, [phase, mode, spScore, p1State.score, p2State.score])

  // ── Start / Restart ───────────────────────────────────────────────────────
  const startGame = () => {
    const newQs = generateQuestions(150, topic, difficulty)
    setQuestions(newQs)
    questionsRef.current = newQs

    setLeftMs(ROUND_DURATION_MS)
    setSpQIdx(0)
    setSpResult(null)
    setSpScore(0)
    setSpCorrectCount(0)
    setSpWrongCount(0)
    spAdvancingRef.current = false

    setP1State({ qIdx: 0, score: 0, result: null, advancing: false, correctCount: 0, wrongCount: 0 })
    setP2State({ qIdx: 0, score: 0, result: null, advancing: false, correctCount: 0, wrongCount: 0 })

    setPhase('playing')
  }

  const topicTitle = topic === 'hrm' ? 'หมวด ห.ร.ม.' : topic === 'krn' ? 'หมวด ค.ร.น.' : 'หมวดสลับ (ห.ร.ม. & ค.ร.น.)'
  const diffTitle  = difficulty === 'easy' ? 'ง่าย' : difficulty === 'hard' ? 'ยาก' : 'ปานกลาง'

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════

  // ── Ready screen ─────────────────────────────────────────────────────────
  if (phase === 'ready') {
    return (
      <div style={{
        width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '2rem',
        background: 'linear-gradient(135deg, #0a0015 0%, #0f0025 100%)',
        fontFamily: 'var(--font-kanit)',
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(ellipse at 30% 40%, rgba(124,58,237,0.12) 0%, transparent 50%),
                            radial-gradient(ellipse at 70% 60%, rgba(6,182,212,0.1) 0%, transparent 50%)`,
          pointerEvents: 'none',
        }} />

        <div className="glass-card animate-scale-in" style={{ padding: '2.5rem 3rem', textAlign: 'center', maxWidth: 440, width: '90%' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
            {mode === '2p' ? '⚔️' : '🧑‍🚀'}
          </div>
          <h1 className="text-shimmer" style={{ fontSize: '1.8rem', fontWeight: 900, marginBottom: '0.2rem' }}>
            {mode === '2p' ? 'ดวลสปีด 2 ผู้เล่น (Tablet Mode)' : 'โหมดเดี่ยว (Tablet Mode)'}
          </h1>
          <div style={{ fontSize: '0.9rem', color: '#c4b5fd', fontWeight: 600, marginBottom: '1.2rem' }}>
            {topicTitle} &nbsp;·&nbsp; ระดับ{diffTitle}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.7 }}>
            {mode === '2p'
              ? <>แตะเลือกคำตอบบนหน้าจอสัมผัส<br/><span className="text-cyan-400 text-xs mt-1 inline-block">📱 รองรับ Multi-Touch แตะตอบพร้อมกันได้ทั้ง 2 ฝั่ง</span></>
              : <>แตะเลือกคำตอบบนหน้าจอสัมผัส<br/><span className="text-emerald-400 text-xs mt-1 inline-block font-mono">⚡ ตอบถูก +2 คะแนน | ตอบผิด -1 คะแนน</span></>
            }
          </p>

          {mode === '2p' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1.2rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>ลักษณะการนั่งเล่น:</span>
              <div className="glass-card" style={{ display: 'flex', padding: 4, gap: 4, border: '1px solid rgba(255,255,255,0.12)' }}>
                <button
                  onClick={() => setSeating('opposite')}
                  style={{
                    padding: '6px 14px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 700,
                    background: seating === 'opposite' ? 'rgba(124,58,237,0.45)' : 'transparent',
                    border: seating === 'opposite' ? '1.5px solid #a78bfa' : '1.5px solid transparent',
                    color: seating === 'opposite' ? '#fff' : 'rgba(255,255,255,0.45)',
                    cursor: 'pointer', transition: 'all 0.25s ease',
                  }}
                >
                  🔄 นั่งตรงข้ามกัน (หันหน้าหากัน)
                </button>
                {!isPortrait && (
                  <button
                    onClick={() => setSeating('side_by_side')}
                    style={{
                      padding: '6px 14px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 700,
                      background: seating === 'side_by_side' ? 'rgba(6,182,212,0.45)' : 'transparent',
                      border: seating === 'side_by_side' ? '1.5px solid #06b6d4' : '1.5px solid transparent',
                      color: seating === 'side_by_side' ? '#fff' : 'rgba(255,255,255,0.45)',
                      cursor: 'pointer', transition: 'all 0.25s ease',
                    }}
                  >
                    👥 นั่งข้างกัน
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', marginBottom: '1.5rem' }}>
            <div>⏱️ เวลาทั้งหมด: <b>60 วินาที (1 นาที)</b></div>
            <div>🎯 ทำคะแนนให้ได้มากที่สุดเท่าที่จะทำได้!</div>
          </div>

          <button
            id="btn-start-game"
            onClick={startGame}
            className="btn-neon"
            style={{
              width: '100%', padding: '0.85rem 2rem',
              borderRadius: 16, fontWeight: 800, fontSize: '1.1rem',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.6), rgba(6,182,212,0.4))',
              border: '1px solid rgba(124,58,237,0.5)',
              color: '#fff', cursor: 'pointer',
              boxShadow: '0 0 30px rgba(124,58,237,0.3)',
            }}
          >
            ▶ เริ่มเกม
          </button>
          <button
            id="btn-back-from-ready"
            onClick={onBackToHome}
            style={{
              marginTop: '0.75rem', width: '100%', padding: '0.6rem',
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
              cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            ← กลับหน้าหลัก
          </button>
        </div>
      </div>
    )
  }

  // ── Finished screen ───────────────────────────────────────────────────────
  if (phase === 'finished') {
    const winner = mode === '2p'
      ? p1State.score > p2State.score ? 'p1' : p2State.score > p1State.score ? 'p2' : 'draw'
      : null

    const rankInfo = mode === '1p' ? getRankTitle(spScore, spCorrectCount) : null
    const spTotal = spCorrectCount + spWrongCount
    const spAcc = spTotal > 0 ? Math.round((spCorrectCount / spTotal) * 100) : 0

    return (
      <div style={{
        width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
        background: 'linear-gradient(135deg, #0a0015 0%, #0f0025 100%)',
        fontFamily: 'var(--font-kanit)',
        overflowY: 'auto',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(ellipse at 50% 40%, rgba(245,158,11,0.12) 0%, transparent 60%)`,
          pointerEvents: 'none',
        }} />

        <div className="glass-card animate-scale-in" style={{ padding: '2.5rem 3rem', textAlign: 'center', maxWidth: 520, width: '90%' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.3rem' }}>🏆</div>
          <h1 className="text-shimmer" style={{ fontSize: '2rem', fontWeight: 900, marginBottom: '0.2rem' }}>
            หมดเวลา 1 นาที!
          </h1>
          <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', marginBottom: '1.2rem' }}>
            {topicTitle} &nbsp;·&nbsp; ระดับ{diffTitle}
          </div>

          {mode === '1p' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
              {/* Score */}
              <div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '0.2rem' }}>คะแนนรวมสุทธิ</p>
                <div style={{
                  fontSize: '3.8rem', fontWeight: 900, color: '#f59e0b',
                  fontFamily: 'var(--font-outfit)',
                  textShadow: '0 0 30px rgba(245,158,11,0.6)',
                }}>
                  {spScore} pts
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="grid grid-cols-3 gap-3 w-full max-w-md my-1">
                <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                  <div className="text-xs text-white/40">ตอบถูก (✓)</div>
                  <div className="text-lg font-black text-emerald-400 font-mono">{spCorrectCount} ข้อ</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                  <div className="text-xs text-white/40">ตอบผิด (✗)</div>
                  <div className="text-lg font-black text-red-400 font-mono">{spWrongCount} ข้อ</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                  <div className="text-xs text-white/40">แม่นยำ (%)</div>
                  <div className="text-lg font-black text-cyan-400 font-mono">{spAcc}%</div>
                </div>
              </div>

              {/* Rank Badge */}
              {rankInfo && (
                <div style={{
                  padding: '0.5rem 1.2rem',
                  borderRadius: 16,
                  background: 'rgba(255,255,255,0.06)',
                  border: `1.5px solid ${rankInfo.color}`,
                  color: rankInfo.color,
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  boxShadow: `0 0 15px ${rankInfo.color}44`,
                }}>
                  {rankInfo.title}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1rem' }}>
                {[
                  { label: 'ผู้เล่น 1 (P1)', score: p1State.score, correct: p1State.correctCount, wrong: p1State.wrongCount, color: '#a78bfa', isWinner: winner === 'p1' },
                  { label: 'ผู้เล่น 2 (P2)', score: p2State.score, correct: p2State.correctCount, wrong: p2State.wrongCount, color: '#06b6d4', isWinner: winner === 'p2' },
                ].map(({ label, score, correct, wrong, color, isWinner }) => (
                  <div
                    key={label}
                    style={{
                      flex: 1,
                      padding: '1rem 0.5rem',
                      borderRadius: 16,
                      background: isWinner ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.03)',
                      border: `1.5px solid ${isWinner ? '#f59e0b' : 'rgba(255,255,255,0.08)'}`,
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>{label}</div>
                    <div style={{
                      fontSize: '2.5rem', fontWeight: 900, color,
                      fontFamily: 'var(--font-outfit)',
                      textShadow: `0 0 20px ${color}88`,
                    }}>
                      {score}
                    </div>
                    <div style={{ fontSize: '0.72rem', marginTop: 4, display: 'flex', justifyContent: 'center', gap: 6 }} className="font-mono">
                      <span className="text-emerald-400">✓{correct}</span>
                      <span className="text-red-400">✗{wrong}</span>
                    </div>
                    {isWinner && <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f59e0b', marginTop: 4 }}>👑 ผู้ชนะ!</div>}
                    {winner === 'draw' && <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>เสมอ</div>}
                  </div>
                ))}
              </div>

              {/* Winner Announcement */}
              <div style={{
                fontSize: '1.1rem', fontWeight: 800,
                color: winner === 'p1' ? '#a78bfa' : winner === 'p2' ? '#06b6d4' : '#94a3b8',
                marginBottom: '0.8rem',
              }}>
                {winner === 'p1' ? '🎉 ผู้เล่น 1 ชนะในการดวลนี้!' : winner === 'p2' ? '🎉 ผู้เล่น 2 ชนะในการดวลนี้!' : '🤝 คะแนนเท่ากัน — เสมอกัน!'}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button
              id="btn-play-again"
              onClick={startGame}
              className="btn-neon cursor-pointer"
              style={{
                flex: 1, padding: '0.75rem 1rem', borderRadius: 14,
                fontWeight: 700, fontSize: '0.95rem',
                background: 'linear-gradient(135deg, rgba(124,58,237,0.5), rgba(6,182,212,0.35))',
                border: '1px solid rgba(124,58,237,0.5)', color: '#fff',
              }}
            >
              🔄 เล่นอีกรอบ
            </button>
            <button
              id="btn-home-from-finish"
              onClick={onBackToHome}
              className="btn-neon cursor-pointer"
              style={{
                flex: 1, padding: '0.75rem 1rem', borderRadius: 14,
                fontWeight: 700, fontSize: '0.95rem',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)', color: '#fff',
              }}
            >
              🏠 หน้าหลัก
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Single Player View ────────────────────────────────────────────────────
  if (mode === '1p') {
    const question = questions[spQIdx]

    return (
      <div style={{
        width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(160deg, #0a0015 0%, #0f0025 50%, #0a001f 100%)',
        fontFamily: 'var(--font-kanit)',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}>
        {/* Grid bg */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `
            linear-gradient(rgba(124,58,237,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(124,58,237,0.04) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }} />

        {/* Header bar */}
        <div style={{
          position: 'relative', zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.5rem 1.5rem',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(0,0,0,0.25)',
          backdropFilter: 'blur(12px)',
          gap: '1rem',
        }}>
          <div className="flex items-center gap-2">
            <button
              id="btn-exit-game"
              onClick={onBackToHome}
              style={{
                padding: '5px 14px', borderRadius: 10,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '0.8rem',
              }}
            >
              ✕ ออก
            </button>
            <button
              onClick={toggleFullscreen}
              style={{
                padding: '5px 12px', borderRadius: 10,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.8rem',
              }}
            >
              {fullscreen ? '📉' : '⛶ เต็มจอ'}
            </button>
          </div>

          {/* 60s Timer Bar Header */}
          <div style={{ flex: 1, maxWidth: '24rem' }}>
            <TimerHeader leftMs={leftMs} />
          </div>

          {/* Score & Counters */}
          <div className="flex items-center gap-3">
            <div className="text-xs text-white/50 hidden sm:flex gap-2 font-mono">
              <span className="text-emerald-400">✓ {spCorrectCount}</span>
              <span className="text-red-400">✗ {spWrongCount}</span>
            </div>

            <div className="score-badge" style={{ padding: '5px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>คะแนน</span>
              <span style={{
                fontSize: '1.3rem', fontWeight: 900, color: '#a78bfa',
                fontFamily: 'var(--font-outfit)',
                textShadow: '0 0 12px rgba(167,139,250,0.5)',
              }}>
                {spScore}
              </span>
            </div>
          </div>
        </div>

        {/* Main area */}
        <div className="flex-1 flex flex-col items-center justify-center gap-4 sm:gap-6 p-4 sm:p-8 relative z-10 overflow-y-auto">
          {question && (
            <>
              <div className="w-full max-w-2xl">
                <QuestionCard question={question} />
              </div>

              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                👉 แตะเลือกคำตอบบนหน้าจอสัมผัส
              </div>

              <div style={{ display: 'flex', gap: 10, width: '100%', maxWidth: '42rem', touchAction: 'none' }}>
                {question.choices.map((choice, i) => {
                  let state = 'idle'
                  if (spResult !== null) {
                    if (i === spResult.choiceIndex) state = spResult.correct ? 'correct' : 'wrong'
                    else if (choice === question.answer) state = 'reveal'
                  }
                  return (
                    <ChoiceButton
                      key={i}
                      id={`p1-sp-choice-${i}`}
                      value={choice}
                      onClick={() => handleSpAnswer(i)}
                      state={state}
                      size="lg"
                    />
                  )
                })}
              </div>

              {/* Reserved Feedback Result Message Area (Fixed height to prevent screen jump) */}
              <div
                style={{
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  visibility: spResult !== null ? 'visible' : 'hidden',
                  transition: 'opacity 0.2s ease',
                  opacity: spResult !== null ? 1 : 0,
                }}
              >
                {spResult && (
                  <div
                    style={{
                      fontSize: '1rem',
                      fontWeight: 800,
                      color: spResult.correct ? '#10b981' : '#ef4444',
                      padding: '5px 18px',
                      borderRadius: '99px',
                      background: spResult.correct ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                      border: `1.5px solid ${spResult.correct ? '#10b98166' : '#ef444466'}`,
                      boxShadow: `0 0 15px ${spResult.correct ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                    }}
                  >
                    {spResult.correct
                      ? `✓ ถูกต้อง! +2 คะแนน`
                      : `✗ ผิด -1 คะแนน (คำตอบที่ถูกคือ ${question.answer})`}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── 2 Players View (Independent progression per player, Multi-Touch Enabled) ──
  const p1Rotate180 = isPortrait || seating === 'opposite'

  return (
    <div style={{
      width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(160deg, #0a0015 0%, #0f0025 50%, #0a001f 100%)',
      fontFamily: 'var(--font-kanit)',
      overflow: 'hidden',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    }}>
      {/* Shared top bar */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '0.4rem 1rem',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(12px)',
        gap: '1rem',
        zIndex: 20,
      }}>
        {/* Back & Fullscreen */}
        <div className="flex items-center gap-2">
          <button
            id="btn-exit-2p"
            onClick={onBackToHome}
            style={{
              padding: '3px 10px', borderRadius: 8, fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.35)', cursor: 'pointer',
            }}
          >
            ✕ ออก
          </button>
          <button
            onClick={toggleFullscreen}
            style={{
              padding: '3px 8px', borderRadius: 8, fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
            }}
          >
            {fullscreen ? '📉' : '⛶'}
          </button>
        </div>

        {/* P1 Score */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#a78bfa', boxShadow: '0 0 6px #a78bfa' }} />
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>P1</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#a78bfa', fontFamily: 'var(--font-outfit)' }}>
            {p1State.score}
          </span>
        </div>

        {/* Shared Timer Header */}
        <div style={{ flex: 1, maxWidth: '20rem' }}>
          <TimerHeader leftMs={leftMs} />
        </div>

        {/* Seating toggle button if landscape */}
        {!isPortrait && (
          <button
            onClick={() => setSeating(s => s === 'opposite' ? 'side_by_side' : 'opposite')}
            style={{
              padding: '3px 10px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 700,
              background: seating === 'opposite' ? 'rgba(124,58,237,0.3)' : 'rgba(6,182,212,0.3)',
              border: `1px solid ${seating === 'opposite' ? '#a78bfa' : '#06b6d4'}`,
              color: '#fff', cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            {seating === 'opposite' ? '🔄 นั่งตรงข้าม' : '👥 นั่งข้างกัน'}
          </button>
        )}

        {/* P2 Score */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#06b6d4', fontFamily: 'var(--font-outfit)' }}>
            {p2State.score}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>P2</span>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#06b6d4', boxShadow: '0 0 6px #06b6d4' }} />
        </div>
      </div>

      {/* Split panel container with Multi-Touch support */}
      {isPortrait ? (
        /* Portrait Mode: Split Top / Bottom */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', touchAction: 'none' }}>
          {/* Top Half: P1 */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.1)', touchAction: 'none' }}>
            <PlayerPanel
              player="p1" playerState={p1State}
              question={questions[p1State.qIdx]}
              rotate180={true}
              onAnswer={handleP1Answer}
            />
          </div>

          {/* Bottom Half: P2 */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', touchAction: 'none' }}>
            <PlayerPanel
              player="p2" playerState={p2State}
              question={questions[p2State.qIdx]}
              rotate180={false}
              onAnswer={handleP2Answer}
            />
          </div>
        </div>
      ) : (
        /* Landscape Mode: Split Left / Right */
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', touchAction: 'none' }}>
          <PlayerPanel
            player="p1" playerState={p1State}
            question={questions[p1State.qIdx]}
            rotate180={p1Rotate180}
            onAnswer={handleP1Answer}
          />
          <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
          <PlayerPanel
            player="p2" playerState={p2State}
            question={questions[p2State.qIdx]}
            rotate180={false}
            onAnswer={handleP2Answer}
          />
        </div>
      )}
    </div>
  )
}
